import {Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {BokkzIbDataService, Gift, IncomeTaxEntity} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormArray, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from 'rxjs';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type GewoneGiftenFormGroup = FormGroup<{
    omschrijving: FormControl<string | null>;
    bedrag: FormControl<number | null>;
    cultureleAnbi: FormControl<boolean | null>;
}>;

@Component({
    selector: 'snelstart-venture-fiscaal-feature-gewone-giften',
    templateUrl: './gewone-giften.component.html',
    styleUrls: ['./gewone-giften.component.scss'],
    imports: [SharedUiButtonModule, ReactiveFormsModule, SharedUiDetailsModule, SharedUiFormControlsModule, CommonModule],
})
export class GewoneGiftenComponent implements OnInit, OnDestroy {
    @Input() public id!: string;
    @Input() searchResults: any[] = [];
    @Input() searchPerformed: boolean = false;
    @Input() resultCount: number = 0;

    @Output() updateSearchResults = new EventEmitter<{
        results: any[];
        performed: boolean;
        count: number;
    }>();

    private dataService = inject(BokkzIbDataService);
    private destroyRef = inject(DestroyRef);
    private changeSubscriptions = new Subscription();
    public showAnbiSearch = false;
    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: true, label: 'Ja' },
        { value: false, label: 'Nee' },
    ];

    public formGroup = new FormGroup({
        gewoneGiften: new FormArray<GewoneGiftenFormGroup>([]),
    });

    readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    public ngOnInit(): void {
        this.incomeTaxEntity$.pipe(
          take(1),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe((data) => {
            this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaAftrekbareGiften?.gewoneGiften || []);
        });
        this.subscribeToChanges();
    }

    ngOnDestroy(): void {
        this.changeSubscriptions.unsubscribe();
    }

    private subscribeToChanges(): void {
        this.changeSubscriptions.unsubscribe();
        this.changeSubscriptions = new Subscription();

        const extractRelevant = () =>
            this.gewoneGiften.getRawValue().map((w) => ({
                bedrag: w.bedrag,
            }));

        this.changeSubscriptions.add(
            this.gewoneGiften.valueChanges
                .pipe(
                    debounceTime(500),
                    tap(() => this.saveForm()),
                    map(() => extractRelevant()),
                    distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
                    switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(),
        );
    }

    get gewoneGiften(): FormArray {
        return this.formGroup.controls.gewoneGiften as FormArray;
    }

    addRij(): void {
        this.gewoneGiften.push(this.createFormGroup(), { emitEvent: false });
        this.showAnbiSearch = true;
    }

    verwijderRij(index: number): void {
        this.gewoneGiften.removeAt(index, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
        this.formGroup.markAsDirty({ emitEvent: false });
        this.saveForm();
    }

    createFormGroup(item?: Gift): FormGroup {
        return new FormGroup({
            omschrijving: new FormControl<string | null>(item?.omschrijving ?? null),
            bedrag: new FormControl<number | null>(item?.bedrag ?? null),
            cultureleAnbi: new FormControl<boolean>(item?.cultureleAnbi ?? false),
        });
    }

    public updateForm(data: Gift[]): void {
        const lijst = data || [];
        const formArray = this.gewoneGiften;

        while (formArray.length > lijst.length) {
            formArray.removeAt(formArray.length - 1, { emitEvent: false });
        }

        for (let i = 0; i < lijst.length; i++) {
            if (i < formArray.length) {
                const control = formArray.at(i) as FormGroup;
                control.patchValue(lijst[i], { emitEvent: false });
            } else {
                const formGroup = this.createFormGroup(lijst[i]);
                formArray.push(formGroup, { emitEvent: false });
            }
        }
    }

    saveForm(): void {
        const items = this.gewoneGiften
            .getRawValue()
            .filter((item) => item.omschrijving != null && item.bedrag !== null)
            .map((gift) => ({
                omschrijving: gift.omschrijving!,
                bedrag: gift.bedrag!,
                cultureleAnbi: gift.cultureleAnbi ?? false,
            }));

        this.dataService.patchIncomeTaxEntity((currentEntity) => {
            if (!currentEntity.aangifte) {
                return currentEntity;
            }

            return {
                ...currentEntity,
                aangifte: {
                    ...currentEntity.aangifte,
                    persoonsgebondenAftrek: {
                        ...currentEntity.aangifte.persoonsgebondenAftrek,
                        pgaAftrekbareGiften: {
                            ...currentEntity.aangifte.persoonsgebondenAftrek?.pgaAftrekbareGiften,
                            gewoneGiften: items,
                        },
                    },
                },
            } as IncomeTaxEntity;
        });
    }
}
